// What a plugin's schedule IS, as lines a card can render — read from Paradigm's
// /status (via GET /api/plugins), never from an automation this app made.
// Paradigm owns plugin configuration and schedules now; this app can only read
// them and link to the page where they are changed.
//
// Pure and dependency-free (.mjs) so node --test can import it directly.

// Append return_to to a URL that may already carry a query (the backend adds
// ?group_id= to setup_url when acting as a group).
export function withReturnTo(url, returnTo) {
  if (!url) return null;
  if (!returnTo) return url;
  return `${url}${url.includes('?') ? '&' : '?'}return_to=${encodeURIComponent(returnTo)}`;
}

function defaultFmtNext(iso, now) {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  const time = d.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit', hour12: false });
  const sameDay = d.toDateString() === new Date(now).toDateString();
  return sameDay ? time : `${d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' })} ${time}`;
}

function onLine(frequency, nextRunAt, fmtNext, now) {
  const parts = ['on'];
  if (frequency) parts.push(String(frequency).replace(/-/g, ' '));
  const next = nextRunAt ? fmtNext(nextRunAt, now) : null;
  if (next) parts.push(`next ${next}`);
  return parts.join(' · ');
}

// Lines, in order. Each: { key, tone: 'on'|'off'|'warn'|'unknown', label?, text,
// action? }. `action` is link text pointing at the plugin's setup page.
//
// Degrades by what /status said: no answer at all → "state unknown"; an older
// Paradigm with no `schedules` → one plugin-level line from enabled/frequency;
// `consented` absent (null) → not treated as "not approved".
// A schedule Paradigm switched off until its owner picks the privacy realm it
// runs under. Said on the card, because a sync that silently stopped reads as
// a plugin finding nothing.
export const NEEDS_REALM = 'off until you choose a privacy realm';

export function scheduleLines(p, { now = Date.now(), fmtNext = defaultFmtNext } = {}) {
  if (!p) return [];
  if (p.status_unknown) {
    return [{ key: 'unknown', tone: 'unknown', text: 'state unknown' }];
  }
  if (p.configured === false) {
    return [{ key: 'setup', tone: 'warn', text: 'not set up', action: 'set up on Paradigm' }];
  }
  if (p.consented === false) {
    return [{ key: 'consent', tone: 'warn', text: 'not approved yet', action: 'approve on Paradigm' }];
  }
  const schedules = Array.isArray(p.schedules) ? p.schedules.filter((s) => s && s.tool_id) : [];
  if (schedules.length) {
    const names = new Map((p.tools || []).map((t) => [t.tool_id, t.name || t.tool_id]));
    return schedules.map((s) => (s.needs_realm ? {
      key: s.tool_id,
      tone: 'warn',
      label: names.get(s.tool_id) || s.tool_id,
      text: NEEDS_REALM,
      action: 'choose a realm on Paradigm',
    } : {
      key: s.tool_id,
      tone: s.enabled ? 'on' : 'off',
      label: names.get(s.tool_id) || s.tool_id,
      text: s.enabled ? onLine(s.frequency, s.next_run_at, fmtNext, now) : 'off',
    }));
  }
  if (p.needs_realm) {
    return [{ key: 'plugin', tone: 'warn', text: NEEDS_REALM, action: 'choose a realm on Paradigm' }];
  }
  return [{
    key: 'plugin',
    tone: p.enabled ? 'on' : 'off',
    text: p.enabled ? onLine(p.frequency, p.next_run_at, fmtNext, now) : 'off',
  }];
}

// What a run may carry: ONLY runtime parameters, and only those a person typed.
// Setup values are saved on Paradigm and merged under the run there ("the run
// wins") — so sending a default from here would override the saved choice.
//
// A `file` parameter is held on the page as the upload's `{ id, filename, ... }`
// and sent as the NODE ID — Paradigm validates it as a uuid, so the object
// itself would be refused. A file still uploading (no id yet) sends nothing.
export function runInputs(plugin, typed) {
  const out = {};
  const values = typed || {};
  for (const param of (plugin && plugin.runtime_params) || []) {
    if (!param || param.secret) continue;
    const v = runtimeValue(param, values[param.key]);
    if (v !== undefined) out[param.key] = v;
  }
  return out;
}

function runtimeValue(param, v) {
  if (v === undefined || v === null) return undefined;
  if (param.type === 'file') {
    const id = typeof v === 'object' ? v.id : v;
    return id && String(id).trim() ? String(id).trim() : undefined;
  }
  if (String(v).trim() === '') return undefined;
  return typeof v === 'string' ? v.trim() : v;
}

// The REQUIRED runtime parameters this run has no value for — a file counts as
// missing until its upload has a node id. Setup fields are Paradigm's to judge
// (/status `missing_fields`), so only runtime ones are asked about here.
export function missingRuntime(plugin, typed) {
  const values = typed || {};
  return ((plugin && plugin.runtime_params) || [])
    .filter((p) => p && !p.secret && p.required && runtimeValue(p, values[p.key]) === undefined);
}

// Why Run cannot start yet, in words, or null. "Choose the chat export first"
// for a file, "Fill in username first" for anything else.
export function missingRunMessage(plugin, typed) {
  const missing = missingRuntime(plugin, typed);
  if (!missing.length) return null;
  const p = missing[0];
  const label = String(p.label || p.key).toLowerCase();
  return p.type === 'file' ? `Choose the ${label} first` : `Fill in ${label} first`;
}

// The plugin's own summary ends "… — continuing" when it imported one batch
// and Paradigm carries on with the rest in the background.
export function continuesInBackground(message) {
  return /continuing/i.test(String(message || ''));
}

// Bytes in words for a file chip: 812 B, 14.2 KB, 3.4 MB.
export function formatBytes(n) {
  const b = Number(n) || 0;
  if (b < 1024) return `${b} B`;
  if (b < 1024 * 1024) return `${(b / 1024).toFixed(1)} KB`;
  return `${(b / (1024 * 1024)).toFixed(1)} MB`;
}

// A legacy automation Paradigm moved onto the plugin's own per-tool schedules
// (`migrated: true` on /process/automations/status). It is off and cannot be
// switched back on here; it resumes once its owner chooses a privacy realm on
// the plugin's setup page. One link per plugin it ran, named when we know it.
export const MOVED_TO_PARADIGM = 'Moved to Paradigm — choose a privacy realm to resume';

export function migratedLinks(automation, plugins, returnTo) {
  if (!automation || !automation.migrated) return null;
  const known = new Map(((plugins || [])).map((p) => [p.id, p.name]));
  return {
    text: MOVED_TO_PARADIGM,
    links: (automation.moved_to || []).filter((m) => m && m.setup_url).map((m) => ({
      plugin_id: m.plugin_id,
      name: known.get(m.plugin_id) || 'plugin setup',
      href: withReturnTo(m.setup_url, returnTo),
    })),
  };
}

// What to say when a toggle is refused. A 409 MIGRATED carries Paradigm's own
// sentence; anything else is a plain failure.
export function toggleFailureMessage(err) {
  const body = (err && err.body) || {};
  if (body.error === 'MIGRATED') return body.message || MOVED_TO_PARADIGM;
  return 'could not change that: ' + ((err && err.message) || 'unknown error');
}
